import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import QuizTeacherView from '../../components/QuizTeacherView';

interface Question {
  id: string;
  type: string;
  subType?: string;
  title: string;
  options: { id: string; text: string; isCorrect?: boolean; image?: string; imageWidth?: number }[];
  score: number;
  titleImages?: string[];
  titleImageWidths?: number[];
  titleImageLayout?: string;
  imagePosition?: string;
  optionLayout?: string;
  explanation?: string;
}

interface Paper {
  id: string;
  title: string;
  total_score: number;
}

interface QuestionStats {
  questionId: string;
  accuracy: number;
  totalAnswers: number;
  correctAnswers: number;
}

interface PaperStats {
  highestScore: number;
  lowestScore: number;
  averageScore: number;
  totalStudents: number;
}

interface PaperRanking {
  rank: number;
  studentId: string;
  studentName: string;
  score: number;
  answerTime: number;
}

export default function PaperReviewDetail() {
  const navigate = useNavigate();
  const { paperId } = useParams<{ paperId: string }>();
  const [searchParams] = useSearchParams();
  const assignmentId = searchParams.get('assignmentId');

  const [loading, setLoading] = useState(true);
  const [paper, setPaper] = useState<Paper | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [paperStats, setPaperStats] = useState<PaperStats | null>(null);
  const [questionStats, setQuestionStats] = useState<QuestionStats[]>([]);
  const [rankings, setRankings] = useState<PaperRanking[]>([]);

  useEffect(() => {
    if (paperId) {
      loadPaperDetail();
    }
  }, [paperId, assignmentId]);

  const loadPaperDetail = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }

      const session = await supabase.auth.getSession();

      // 如果有 assignmentId，使用作业维度的评讲接口
      const action = assignmentId ? 'get_assignment_paper_detail' : 'get_paper_detail';
      const body = assignmentId
        ? { action, paperId, assignmentId }
        : { action, paperId };

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/teacher-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.data.session?.access_token}`,
        },
        body: JSON.stringify(body),
      });

      const result = await response.json();
      if (result.success) {
        setPaper(result.data.paper);

        // 转换题目数据格式以匹配 Quiz 页面
        const rawQuestions = result.data.questions || [];
        const mappedQuestions: Question[] = rawQuestions.map((q: any) => ({
          id: q.id,
          type: q.question_type || q.type || 'single_choice',
          subType: q.sub_type || q.subType,
          title: q.content || q.title,
          options: (q.options || []).map((opt: any) => ({
            id: opt.id || opt.label,
            text: opt.text,
            isCorrect: opt.isCorrect,
            image: opt.image,
            imageWidth: opt.imageWidth || 100
          })),
          score: q.score || 2,
          titleImages: q.title_images || q.titleImages || [],
          titleImageWidths: q.title_image_widths || q.titleImageWidths || [],
          titleImageLayout: q.title_image_layout || q.titleImageLayout || 'auto',
          imagePosition: q.image_position || q.imagePosition || 'below',
          optionLayout: q.option_layout || q.optionLayout || 'grid',
          explanation: q.explanation
        }));

        setQuestions(mappedQuestions);
        setPaperStats(result.data.paperStats);
        setQuestionStats(result.data.questionStats || []);
        setRankings(result.data.rankings || []);
      }
    } catch (error) {
      console.error('加载试卷详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!paper || questions.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center text-gray-500">
          <BookOpen className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p>试卷不存在或无权限访问</p>
          <button
            onClick={() => assignmentId ? navigate('/teacher/assignments') : navigate('/teacher/paper-review')}
            className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            {assignmentId ? '返回作业管理' : '返回列表'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <QuizTeacherView
      paper={paper}
      questions={questions}
      paperStats={paperStats!}
      questionStats={questionStats}
      rankings={rankings}
      onBack={() => assignmentId ? navigate('/teacher/assignments') : navigate('/teacher/paper-review')}
      onViewStudentDetail={(studentId) => navigate(`/teacher/exam-records?studentId=${studentId}`)}
    />
  );
}
